// Client-side CAS uploader (Phase 2b). Replaces the dead SDK snack uploader
// (`/--/api/v2/snack/uploadAsset`, which 404s in our stack) for FileList
// imports: hash the file locally → /cas/prepare → (inline commit for small, or
// presigned PUT + commit for large) → return the same-origin CAS URL. The URL
// is absolute (window origin) so the SDK's CodeMessageBuilder uses it as-is and
// the run-engine fetches it same-origin (no CORS). See docs/ASSETS-AND-CAS.md.

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function toBase64(bytes: Uint8Array): string {
  let bin = '';
  const CHUNK = 0x8000; // avoid arg-count limits on String.fromCharCode
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + CHUNK)) as unknown as number[],
    );
  }
  return btoa(bin);
}

/**
 * Upload a picked File to the content-addressed store and return its canonical
 * same-origin URL: `${origin}/api/apps/${appId}/~asset/cas/<sha256>`.
 * Idempotent: an already-stored file (`present`) uploads 0 bytes.
 */
export async function uploadAssetToCas(appId: string, file: File): Promise<string> {
  const origin = window.location.origin;
  const base = `${origin}/api/apps/${encodeURIComponent(appId)}`;
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const hash = await sha256Hex(buf);
  const mime = file.type || 'application/octet-stream';

  const prep = await fetch(`${base}/cas/prepare`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ files: [{ hash, mime, size: bytes.length }] }),
  });
  if (!prep.ok) throw new Error(`cas/prepare ${prep.status}`);
  const pj = (await prep.json()) as {
    results?: { hash: string; status: string; uploadUrl?: string }[];
  };
  const r = pj.results?.[0];

  if (r?.status === 'presign' && r.uploadUrl) {
    const put = await fetch(r.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': mime },
      body: bytes,
    });
    if (!put.ok) throw new Error(`cas PUT ${put.status}`);
    const commit = await fetch(`${base}/cas/commit`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ uploaded: [hash] }),
    });
    if (!commit.ok) throw new Error(`cas/commit(uploaded) ${commit.status}`);
  } else if (r?.status !== 'present') {
    const commit = await fetch(`${base}/cas/commit`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ inline: [{ hash, mime, contentsBase64: toBase64(bytes) }] }),
    });
    if (!commit.ok) throw new Error(`cas/commit(inline) ${commit.status}`);
  }

  return `${base}/~asset/cas/${hash}`;
}

/** Resolve the current appId from query/URL (mirrors App.tsx's resolver). */
export function currentAppId(query?: Record<string, unknown>): string | null {
  const fromQuery = query && typeof query.appId === 'string' ? query.appId : null;
  if (fromQuery) return fromQuery;
  if (typeof window !== 'undefined')
    return new URLSearchParams(window.location.search).get('appId');
  return null;
}
